import { useState, useCallback } from 'react'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { AdminLayout } from '~/components/AdminLayout'
import { useOrder } from '~/api/orders'
import { useUpdateOrderStatus, useAssignDriver, useOrderStatusHistory, type OrderStatus } from '~/api/admin-orders'
import { useAvailableDrivers } from '~/api/drivers'
import type { CogsAllocationSource, CogsFulfillmentMode, CogsReportingState } from '@shinkansen/graphql'
import { getSafePaymentActionUrl } from '~/utils/paymentUrls'
import styles from './order-detail.module.css'

/** Well-known system user ID for automated actions (webhooks, etc.) */
const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000001'

/**
 * Human-readable status labels
 */
const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  PREPARING: 'Preparing',
  READY_FOR_PICKUP: 'Ready for Pickup',
  IN_TRANSIT: 'In Transit',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
}

const COGS_FULFILLMENT_MODE_LABELS: Record<CogsFulfillmentMode, string> = {
  ALLOCATED: 'ALLOCATED',
  UNALLOCATED: 'UNALLOCATED',
}

const COGS_ALLOCATION_SOURCE_LABELS: Record<CogsAllocationSource, string> = {
  NOT_ALLOCATED: 'NOT ALLOCATED',
  STOCK_MOVEMENT: 'STOCK MOVEMENT',
}

const COGS_REPORTING_STATE_LABELS: Record<CogsReportingState, string> = {
  COGS_READY: 'COGS READY',
  PAID_AWAITING_COGS: 'PAID / AWAITING COGS',
  WAITING_PAYMENT: 'WAITING PAYMENT',
}

/**
 * Get the CSS class for an order status badge
 */
function getStatusClass(status: string): string {
  switch (status) {
    case 'PENDING':
      return styles.statusPending
    case 'CONFIRMED':
      return styles.statusConfirmed
    case 'PREPARING':
      return styles.statusPreparing
    case 'READY_FOR_PICKUP':
      return styles.statusReadyForPickup
    case 'IN_TRANSIT':
      return styles.statusInTransit
    case 'DELIVERED':
      return styles.statusDelivered
    case 'CANCELLED':
      return styles.statusCancelled
    default:
      return ''
  }
}

/**
 * Get the CSS class for a payment status badge
 */
function getPaymentStatusClass(status: string | undefined): string {
  switch (status) {
    case 'PENDING':
      return styles.paymentPending
    case 'PAID':
      return styles.paymentPaid
    case 'EXPIRED':
      return styles.paymentExpired
    case 'FAILED':
      return styles.paymentFailed
    case 'REFUNDED':
      return styles.paymentRefunded
    default:
      return styles.paymentUnknown
  }
}

/**
 * Format payment status for display
 */
function formatPaymentStatus(status: string | undefined): string {
  if (!status) return 'Not Available'
  return status.replace(/_/g, ' ')
}

/**
 * Format payment method for display
 */
function formatPaymentMethod(method: string): string {
  const labels: Record<string, string> = {
    VIRTUAL_ACCOUNT: 'Virtual Account',
    E_WALLET: 'E-Wallet',
    QR_CODE: 'QR Code',
    RETAIL_OUTLET: 'Retail Outlet',
    CREDIT_CARD: 'Credit Card',
  }
  return labels[method] || method.replace(/_/g, ' ')
}

function formatPaymentProvider(provider: string | null | undefined): string {
  if (!provider) return 'Not Available'
  return provider.replace(/_/g, ' ')
}

/**
 * Format status for display (replace underscores with spaces)
 */
function formatStatus(status: string): string {
  return status.replace(/_/g, ' ')
}

function formatCogsFulfillmentMode(mode: CogsFulfillmentMode): string {
  return COGS_FULFILLMENT_MODE_LABELS[mode]
}

function formatCogsAllocationSource(source: CogsAllocationSource): string {
  return COGS_ALLOCATION_SOURCE_LABELS[source]
}

function formatCogsReportingState(state: CogsReportingState): string {
  return COGS_REPORTING_STATE_LABELS[state]
}

/**
 * Format amount in IDR currency
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * Format date to readable string with time
 */
function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format relative time (e.g., "2 hours ago", "just now")
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHour < 24) return `${diffHour}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return formatDateTime(dateString)
}

/**
 * Get actor label for status history
 */
function getActorLabel(userId: string): string {
  if (userId === SYSTEM_USER_ID) return 'SYSTEM'
  return userId.slice(0, 8).toUpperCase()
}

/**
 * Admin Order Detail Page - Shows order details with status management
 */
export function AdminOrderDetailPage() {
  const navigate = useNavigate()
  const { orderId } = useParams({ strict: false }) as { orderId: string }
  const { data: order, isLoading, isError, error } = useOrder(orderId)
  const { data: availableDrivers } = useAvailableDrivers()
  const { data: statusHistory } = useOrderStatusHistory(orderId)
  const updateStatusMutation = useUpdateOrderStatus()
  const assignDriverMutation = useAssignDriver()
  
  const [selectedDriverId, setSelectedDriverId] = useState<string>('')
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const isPending = updateStatusMutation.isPending || assignDriverMutation.isPending

  // Handle driver selection change
  const handleDriverChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDriverId(e.target.value)
    setFeedback(null)
  }, [])

  // Handle status transition
  const handleAction = useCallback(async (newStatus: OrderStatus) => {
    if (!order) return

    setFeedback(null)
    
    try {
      await updateStatusMutation.mutateAsync({
        orderId: order.id,
        status: newStatus,
      })
      setFeedback({ type: 'success', message: `Order moved to ${STATUS_LABELS[newStatus]}` })
    } catch (err) {
      setFeedback({ 
        type: 'error', 
        message: err instanceof Error ? err.message : 'Failed to update order' 
      })
    }
  }, [order, updateStatusMutation])

  // Handle driver assignment (separate from status change)
  const handleAssignDriver = useCallback(async () => {
    if (!order || !selectedDriverId) return

    setFeedback(null)
    
    try {
      await assignDriverMutation.mutateAsync({
        orderId: order.id,
        driverId: selectedDriverId,
      })
      setFeedback({ type: 'success', message: 'Driver assigned successfully' })
      setSelectedDriverId('')
    } catch (err) {
      setFeedback({ 
        type: 'error', 
        message: err instanceof Error ? err.message : 'Failed to assign driver' 
      })
    }
  }, [order, selectedDriverId, assignDriverMutation])

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate({ to: '/admin/orders' })
  }, [navigate])

  if (isLoading) {
    return (
      <AdminLayout title="ORDER DETAIL">
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <span className={styles.loadingText}>Loading order...</span>
        </div>
      </AdminLayout>
    )
  }

  if (isError || !order) {
    return (
      <AdminLayout title="ORDER DETAIL">
        <div className={styles.errorContainer}>
          <span className={styles.errorText}>
            {error?.message || 'Order not found'}
          </span>
          <button className={styles.backButton} onClick={handleBack}>
            ← BACK TO ORDERS
          </button>
        </div>
      </AdminLayout>
    )
  }

  // Determine what action is available based on current status
  const isTerminalStatus = order?.status === 'DELIVERED' || order?.status === 'CANCELLED'
  const hasDriver = !!order?.assignedDriverId
  const isAssignedInTransit = order?.status === 'IN_TRANSIT' && hasDriver
  const paymentActionUrl = getSafePaymentActionUrl(order.paymentUrl)
  const hasPaymentDetails = Boolean(
    order.paymentProvider ||
    order.paymentProviderReference ||
    order.paymentMethod ||
    order.paymentChannel ||
    order.paymentTransactionId ||
    order.paymentInstructions ||
    paymentActionUrl ||
    order.paymentExpiresAt ||
    order.paidAt
  )

  return (
    <AdminLayout title="ORDER DETAIL">
      <div className={styles.orderDetailPage}>
        {/* Back Navigation */}
        <div className={styles.backNav}>
          <Link to="/admin/orders" className={styles.backLink}>
            ← BACK TO ORDERS
          </Link>
        </div>

        {/* Order Header */}
        <header className={styles.orderHeader}>
          <h1 className={styles.orderId}>ORDER #{order.externalId}</h1>
          <span className={`${styles.statusBadge} ${getStatusClass(order.status)}`}>
            {formatStatus(order.status)}
          </span>
        </header>

        {/* Section 01: Order Details */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>01</span>
            <h2 className={styles.sectionTitle}>ORDER DETAILS</h2>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Customer</span>
              <span className={styles.detailValue}>{order.customerId.slice(0, 8)}...</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Subtotal</span>
              <span className={styles.detailValue}>{formatCurrency(order.totalAmount)}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Delivery Fee</span>
              <span className={styles.detailValue}>{formatCurrency(order.deliveryFee)}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Total</span>
              <span className={styles.detailValueHighlight}>{formatCurrency(order.totalAmount + order.deliveryFee)}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Address</span>
              <span className={styles.detailValue}>{order.deliveryAddress}</span>
            </div>
            {order.notes && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Notes</span>
                <span className={styles.detailValue}>{order.notes}</span>
              </div>
            )}
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Created</span>
              <span className={styles.detailValue}>{formatDateTime(order.createdAt)}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Updated</span>
              <span className={styles.detailValue}>{formatDateTime(order.updatedAt)}</span>
            </div>
          </div>
        </section>

        {/* Section 02: Order Actions - Unified workflow */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>02</span>
            <h2 className={styles.sectionTitle}>ORDER ACTIONS</h2>
          </div>
          <div className={styles.sectionContent}>
            {isTerminalStatus ? (
              <div className={styles.terminalStatus}>
                <span className={styles.terminalText}>
                  This order is complete ({formatStatus(order.status)})
                </span>
              </div>
            ) : (
              <div className={styles.actionFlow}>
                {/* Show assigned driver if exists */}
                {hasDriver && (
                  <div className={styles.assignedDriver}>
                    <span className={styles.assignedLabel}>ASSIGNED DRIVER</span>
                    <span className={styles.assignedValue}>{order.assignedDriverId?.slice(0, 8)}...</span>
                  </div>
                )}

                {/* PENDING → Show Confirm or Cancel */}
                {order.status === 'PENDING' && (
                  <div className={styles.actionButtons}>
                    <button
                      className={styles.primaryAction}
                      onClick={() => handleAction('CONFIRMED')}
                      disabled={isPending}
                    >
                      {isPending ? 'PROCESSING...' : 'CONFIRM ORDER'}
                    </button>
                    <button
                      className={styles.dangerAction}
                      onClick={() => handleAction('CANCELLED')}
                      disabled={isPending}
                    >
                      CANCEL
                    </button>
                  </div>
                )}

                {/* CONFIRMED → Show Start Preparing or Cancel */}
                {order.status === 'CONFIRMED' && (
                  <div className={styles.actionButtons}>
                    <button
                      className={styles.primaryAction}
                      onClick={() => handleAction('PREPARING')}
                      disabled={isPending}
                    >
                      {isPending ? 'PROCESSING...' : 'START PREPARING'}
                    </button>
                    <button
                      className={styles.dangerAction}
                      onClick={() => handleAction('CANCELLED')}
                      disabled={isPending}
                    >
                      CANCEL
                    </button>
                  </div>
                )}

                {/* PREPARING → Show Ready for Pickup or Cancel */}
                {order.status === 'PREPARING' && (
                  <div className={styles.actionButtons}>
                    <button
                      className={styles.primaryAction}
                      onClick={() => handleAction('READY_FOR_PICKUP')}
                      disabled={isPending}
                    >
                      {isPending ? 'PROCESSING...' : 'READY FOR PICKUP'}
                    </button>
                    <button
                      className={styles.dangerAction}
                      onClick={() => handleAction('CANCELLED')}
                      disabled={isPending}
                    >
                      CANCEL
                    </button>
                  </div>
                )}

                {/* READY_FOR_PICKUP → Assign driver (driver starts delivery from Hayabusa) */}
                {order.status === 'READY_FOR_PICKUP' && (
                  <div className={styles.dispatchFlow}>
                    {hasDriver ? (
                      <>
                        <div className={styles.driverHint}>
                          Driver will start delivery from Hayabusa app
                        </div>
                        <div className={styles.reassignFlow}>
                          <span className={styles.flowLabel}>REASSIGN DRIVER</span>
                          <div className={styles.driverControls}>
                            <select
                              className={styles.driverSelect}
                              value={selectedDriverId}
                              onChange={handleDriverChange}
                              aria-label="Select driver"
                              disabled={!availableDrivers?.length || isPending}
                            >
                              <option value="">Select different driver...</option>
                              {availableDrivers?.filter(d => d.id !== order.assignedDriverId).map((driver) => (
                                <option key={driver.id} value={driver.id}>
                                  {driver.licenseNumber} • {driver.vehicleType} • {driver.vehiclePlate}
                                </option>
                              ))}
                            </select>
                            <button
                              className={styles.assignButton}
                              onClick={() => handleAssignDriver()}
                              disabled={!selectedDriverId || isPending}
                            >
                              {isPending ? 'ASSIGNING...' : 'REASSIGN'}
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className={styles.flowLabel}>ASSIGN DRIVER</span>
                        <div className={styles.driverControls}>
                          <select
                            className={styles.driverSelect}
                            value={selectedDriverId}
                            onChange={handleDriverChange}
                            aria-label="Select driver"
                            disabled={!availableDrivers?.length || isPending}
                          >
                            <option value="">
                              {availableDrivers?.length 
                                ? 'Select driver...' 
                                : 'No available drivers'}
                            </option>
                            {availableDrivers?.map((driver) => (
                              <option key={driver.id} value={driver.id}>
                                {driver.licenseNumber} • {driver.vehicleType} • {driver.vehiclePlate}
                              </option>
                            ))}
                          </select>
                          <button
                            className={styles.assignButton}
                            onClick={() => handleAssignDriver()}
                            disabled={!selectedDriverId || isPending}
                          >
                              {isPending ? 'ASSIGNING...' : 'ASSIGN DRIVER'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* IN_TRANSIT → Mark as Delivered */}
                {order.status === 'IN_TRANSIT' && !isAssignedInTransit && (
                  <div className={styles.actionButtons}>
                    <button
                      className={styles.successAction}
                      onClick={() => handleAction('DELIVERED')}
                      disabled={isPending}
                    >
                      {isPending ? 'PROCESSING...' : 'MARK DELIVERED'}
                    </button>
                  </div>
                )}
                {isAssignedInTransit && (
                  <p className={styles.flowHint}>
                    Complete this delivery from the active route.
                  </p>
                )}
              </div>
            )}
            {feedback && (
              <div className={`${styles.feedback} ${feedback.type === 'success' ? styles.feedbackSuccess : styles.feedbackError}`}>
                {feedback.message}
              </div>
            )}
          </div>
        </section>

        {/* Section 03: Status History */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>03</span>
            <h2 className={styles.sectionTitle}>STATUS HISTORY</h2>
          </div>
          <div className={styles.sectionContent}>
            {statusHistory && statusHistory.length > 0 ? (
              <div className={styles.timeline}>
                {statusHistory.map((entry, index) => (
                  <div key={entry.id} className={styles.timelineEntry}>
                    <div className={styles.timelineConnector}>
                      <div className={`${styles.timelineDot} ${index === statusHistory.length - 1 ? styles.timelineDotActive : ''}`} />
                      {index < statusHistory.length - 1 && <div className={styles.timelineLine} />}
                    </div>
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineHeader}>
                        <span className={`${styles.statusBadge} ${getStatusClass(entry.toStatus)}`}>
                          {formatStatus(entry.toStatus)}
                        </span>
                        <span className={styles.timelineTime} title={formatDateTime(entry.changedAt)}>
                          {formatRelativeTime(entry.changedAt)}
                        </span>
                      </div>
                      <div className={styles.timelineActor}>
                        {entry.fromStatus ? (
                          <>
                            <span className={styles.timelineActorLabel}>
                              {getActorLabel(entry.changedByUserId)}
                            </span>
                            <span className={styles.timelineTransition}>
                              changed from {formatStatus(entry.fromStatus)}
                            </span>
                          </>
                        ) : (
                          <span className={styles.timelineActorLabel}>
                            {entry.notes || 'Order created'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyTimeline}>
                <span className={styles.emptyTimelineText}>No status history available</span>
              </div>
            )}
          </div>
        </section>

        {/* Section 04: Payment & Invoice */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>04</span>
            <h2 className={styles.sectionTitle}>PAYMENT & INVOICE</h2>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.paymentGrid}>
              {/* Left column: Status and Amount */}
              <div className={styles.paymentPrimary}>
                <div className={styles.paymentStatusBlock}>
                  <span className={`${styles.paymentBadgeLarge} ${getPaymentStatusClass(order.paymentStatus ?? undefined)}`}>
                    {formatPaymentStatus(order.paymentStatus ?? undefined)}
                  </span>
                </div>
                <div className={styles.paymentAmountBlock}>
                  <span className={styles.paymentAmountLabel}>TOTAL</span>
                  <span className={styles.paymentAmountValue}>{formatCurrency(order.totalAmount + order.deliveryFee)}</span>
                </div>
              </div>
              
              {/* Right column: Details */}
              <div className={styles.paymentDetails}>
                {order.paymentProvider && (
                  <div className={styles.paymentDetailRow}>
                    <span className={styles.paymentDetailLabel}>PROVIDER</span>
                    <span className={styles.paymentDetailValue}>{formatPaymentProvider(order.paymentProvider)}</span>
                  </div>
                )}
                {order.paymentProviderReference && (
                  <div className={styles.paymentDetailRow}>
                    <span className={styles.paymentDetailLabel}>REFERENCE</span>
                    <span className={styles.paymentDetailValueMono}>{order.paymentProviderReference}</span>
                  </div>
                )}
                {order.paymentMethod && (
                  <div className={styles.paymentDetailRow}>
                    <span className={styles.paymentDetailLabel}>METHOD</span>
                    <span className={styles.paymentDetailValue}>{formatPaymentMethod(order.paymentMethod)}</span>
                  </div>
                )}
                {order.paymentChannel && (
                  <div className={styles.paymentDetailRow}>
                    <span className={styles.paymentDetailLabel}>CHANNEL</span>
                    <span className={styles.paymentDetailValue}>{order.paymentChannel}</span>
                  </div>
                )}
                {order.paymentTransactionId && (
                  <div className={styles.paymentDetailRow}>
                    <span className={styles.paymentDetailLabel}>TRANSACTION</span>
                    <span className={styles.paymentDetailValueMono}>{order.paymentTransactionId}</span>
                  </div>
                )}
                {order.paymentExpiresAt && (
                  <div className={styles.paymentDetailRow}>
                    <span className={styles.paymentDetailLabel}>EXPIRES</span>
                    <span className={styles.paymentDetailValue}>{formatDateTime(order.paymentExpiresAt)}</span>
                  </div>
                )}
                {order.paidAt && (
                  <div className={styles.paymentDetailRow}>
                    <span className={styles.paymentDetailLabel}>PAID AT</span>
                    <span className={styles.paymentDetailValue}>{formatDateTime(order.paidAt)}</span>
                  </div>
                )}
                {!hasPaymentDetails && (
                  <div className={styles.paymentDetailRow}>
                    <span className={styles.paymentDetailMuted}>Payment details will appear after payment</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className={styles.invoiceActions}>
              <Link
                to="/admin/orders/$orderId/invoice"
                params={{ orderId: order.id }}
                className={styles.invoiceButton}
              >
                VIEW INVOICE
              </Link>
              {paymentActionUrl && (
                <a
                  href={paymentActionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.providerActionButton}
                >
                  OPEN PAYMENT ACTION →
                </a>
              )}
            </div>
          </div>
        </section>

        {/* Section 05: COGS Allocation */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>05</span>
            <h2 className={styles.sectionTitle}>COGS ALLOCATION</h2>
          </div>
          <div className={styles.sectionContent}>
            <div className={styles.cogsGrid}>
              <div className={styles.cogsMetric}>
                <span className={styles.cogsLabel}>FULFILLMENT MODE</span>
                <strong>{formatCogsFulfillmentMode(order.cogsAllocation.fulfillmentMode)}</strong>
              </div>
              <div className={styles.cogsMetric}>
                <span className={styles.cogsLabel}>ALLOCATION SOURCE</span>
                <strong>{formatCogsAllocationSource(order.cogsAllocation.allocationSource)}</strong>
              </div>
              <div className={styles.cogsMetric}>
                <span className={styles.cogsLabel}>REPORTING STATE</span>
                <strong>{formatCogsReportingState(order.cogsAllocation.reportingState)}</strong>
              </div>
            </div>
            <p className={styles.cogsNote}>
              COGS readiness requires payment plus a persisted sale stock movement. Orders without stock movement allocation
              stay awaiting COGS.
            </p>
          </div>
        </section>
      </div>
    </AdminLayout>
  )
}
